// data.js — Assessment metadata + index helpers for the Executive Summary.
//
// Scores are NOT hard-coded here anymore: the backend returns per-assessment
// org aggregates (eval / prior / responses) keyed by `assessmentId`, which the
// page merges in at runtime. This module holds the static metadata, the ported
// Vasana band ranges, and the math helpers.
//
// Scale: every assessment's `score` is `evaluated_score` (0-100). `aligned`
// (used for the Wellbeing Index, higher = healthier) inverts the two
// "higher = worse" assessments — Burnout (39) and Moral Injury (31).

import { DD, ddSystemFor, ddBandColor } from "./dd-data";

export const TYPE_COLOR = {
  RE: "var(--vas-re)", // blue
  GA: "var(--vas-ga)", // orange
  SA: "var(--vas-sa)", // purple
  MA: "var(--vas-ma)", // green
};

// ---- Band ranges (ported from vasana-be assessment_graph_ranges.py) ----
// Each range {min,max,color,label} is matched against the 0-100 `score`.
const C = {
  green: "#11953A",
  yellow: "#EDBF09",
  orange: "#DE7116",
  red: "#FF5C5C",
  darkRed: "#8D1212",
  purple: "#8B3F9E",
  blue: "#1976CE",
  orange2: "#ED6708",
  red2: "#DF2D29",
};

const RANGES = {
  sleep: [
    { min: 0, max: 24, color: C.red, label: "Severe" },
    { min: 25, max: 49, color: C.orange, label: "Moderate" },
    { min: 50, max: 74, color: C.yellow, label: "Sub-threshold" },
    { min: 75, max: 100, color: C.green, label: "No Insomnia" },
  ],
  alcohol: [
    { min: 0, max: 64, color: C.red, label: "Dependence" },
    { min: 65, max: 82, color: C.orange, label: "Hazardous" },
    { min: 83, max: 100, color: C.green, label: "Low Risk" },
  ],
  anxiety: [
    { min: 0, max: 32, color: C.red, label: "High" },
    { min: 33, max: 56, color: C.orange, label: "Moderate" },
    { min: 57, max: 80, color: C.yellow, label: "Mild" },
    { min: 81, max: 100, color: C.green, label: "Low" },
  ],
  stress: [
    { min: 0, max: 58, color: C.red, label: "Severe" },
    { min: 59, max: 60, color: C.orange, label: "Likely" },
    { min: 61, max: 67, color: C.yellow, label: "Possible" },
    { min: 68, max: 100, color: C.green, label: "None" },
  ],
  support: [
    { min: 0, max: 32, color: C.red, label: "Low" },
    { min: 33, max: 67, color: C.orange, label: "Moderate" },
    { min: 68, max: 100, color: C.green, label: "Strong" },
  ],
  depression: [
    { min: 0, max: 29, color: C.darkRed, label: "Severe" },
    { min: 30, max: 45, color: C.red, label: "Mod. Severe" },
    { min: 46, max: 64, color: C.orange, label: "Moderate" },
    { min: 65, max: 84, color: C.yellow, label: "Mild" },
    { min: 85, max: 100, color: C.green, label: "Minimal" },
  ],
  // ProQOL (assessment 6) uses the burnout-dimension range set in vasana-be.
  proqol: [
    { min: 0, max: 17, color: C.red, label: "High Burnout" },
    { min: 18, max: 55, color: C.orange, label: "Average" },
    { min: 56, max: 100, color: C.green, label: "Low Burnout" },
  ],
  // Moral Injury: score (= eval) is higher = worse, so high = red.
  moral: [
    { min: 0, max: 19, color: C.purple, label: "Minimal" },
    { min: 20, max: 39, color: C.blue, label: "Low" },
    { min: 40, max: 59, color: C.yellow, label: "Moderate" },
    { min: 60, max: 79, color: C.orange2, label: "High" },
    { min: 80, max: 100, color: C.red2, label: "Severe" },
  ],
  // Capability & Follow-Up: higher = better.
  capability: [
    { min: 0, max: 19, color: C.red2, label: "Very Low" },
    { min: 20, max: 39, color: C.orange2, label: "Low" },
    { min: 40, max: 59, color: C.yellow, label: "Moderate" },
    { min: 60, max: 79, color: C.blue, label: "Strong" },
    { min: 80, max: 100, color: C.purple, label: "Excellent" },
  ],
  // Wellness Readiness (no official ranges) — generic healthy direction.
  wellness: [
    { min: 0, max: 49, color: C.red, label: "Low" },
    { min: 50, max: 79, color: C.yellow, label: "Moderate" },
    { min: 80, max: 100, color: C.green, label: "Strong" },
  ],
  // Burnout (no official ranges) — score (= eval) treated clinical, higher = worse.
  burnout: [
    { min: 0, max: 39, color: C.green, label: "Low" },
    { min: 40, max: 59, color: C.yellow, label: "Moderate" },
    { min: 60, max: 79, color: C.orange, label: "Elevated" },
    { min: 80, max: 100, color: C.red, label: "Severe" },
  ],
};

const GOOD_COLORS = new Set([C.green, C.purple, C.blue]);
const RISK_COLORS = new Set([C.orange, C.orange2, C.red, C.red2, C.darkRed]);

function toneFromColor(color) {
  if (GOOD_COLORS.has(color)) return "good";
  if (RISK_COLORS.has(color)) return "risk";
  return "watch"; // yellow
}

// Resolve a 0-100 score to its band {label, color, tone} for an assessment.
export function resolveBand(score, ranges) {
  if (score == null || !ranges) return { label: "—", color: "#8a8993", tone: "watch" };
  const r = ranges.find((x) => score >= x.min && score <= x.max);
  if (!r) return { label: "—", color: "#8a8993", tone: "watch" };
  return { label: r.label, color: r.color, tone: toneFromColor(r.color) };
}

// ---- Assessment metadata (no scores; merged with live data at runtime) ----
// V2: assessments surface under friendly "Indicator" names and are organized into
// Behavioral / Clinical groups. ids / assessmentId / ranges / invert are UNCHANGED —
// they key the live backend merge (see pages/ExecutiveSummary). Moral Injury,
// Capability, and Follow-Up are not shown in V2, so they are omitted here (their
// backend queries are untouched).
export const ASSESSMENTS = [
  {
    id: "vasana", assessmentId: 1, name: "Vasana Type", abbr: "Behavioral Genome", group: "Behavioral",
    categorical: true, domain: "Disposition",
    desc: "How the organization naturally moves under pressure — the disposition mix across Sa / Re / Ga / Ma. A categorical profile, not a 0–100 score.",
    measures: "A categorical disposition profile (Sa / Re / Ga / Ma), not a 0–100 health score. Excluded from the readiness index.",
  },
  {
    id: "wellness", assessmentId: 30, name: "Readiness Indicator", abbr: "", group: "Behavioral",
    domain: "General", invert: false, ranges: RANGES.wellness,
    desc: "A composite read on day-to-day readiness and wellbeing across the population.",
    measures: "Vasana Readiness Indicator. Higher = healthier.",
  },
  {
    id: "insomnia", assessmentId: 4, name: "Sleep Quality Indicator", abbr: "", group: "Clinical",
    domain: "Recovery", invert: false, ranges: RANGES.sleep,
    desc: "Sleep quality across the population — the upstream lever for anxiety and burnout.",
    measures: "Insomnia Severity Index (7-item). Aligned so higher = healthier sleep.",
  },
  {
    id: "anxiety", assessmentId: 9, name: "Anxiety Severity Indicator", abbr: "", group: "Clinical",
    domain: "Mental health", invert: false, ranges: RANGES.anxiety,
    desc: "Generalized anxiety load across the population.",
    measures: "Generalized Anxiety Disorder scale (7-item). Aligned so higher = healthier.",
  },
  {
    id: "depression", assessmentId: 5, name: "Happiness Levels Indicator", abbr: "", group: "Clinical",
    domain: "Mental health", invert: false, ranges: RANGES.depression,
    desc: "Depressive load across the population.",
    measures: "Patient Health Questionnaire (9-item). Aligned so higher = healthier.",
  },
  {
    id: "ptsd", assessmentId: 3, name: "Stress Presence Indicator", abbr: "", group: "Clinical",
    domain: "Trauma", invert: false, ranges: RANGES.stress,
    desc: "Possible trauma symptoms at a population level — relevant given operational exposure.",
    measures: "PTSD Checklist for DSM-5. Aligned so higher = healthier.",
  },
  {
    id: "support", assessmentId: 8, name: "Social Support Indicator", abbr: "", group: "Clinical",
    domain: "Resilience", invert: false, ranges: RANGES.support,
    desc: "Perceived social support — a protective strength holding teams together under load.",
    measures: "Multidimensional Scale of Perceived Social Support. Higher = stronger support.",
  },
  {
    id: "alcohol", assessmentId: 7, name: "Alcohol Consumption Indicator", abbr: "", group: "Clinical",
    domain: "Behavioral", invert: false, ranges: RANGES.alcohol,
    desc: "Alcohol-related risk across the population.",
    measures: "Alcohol Use Disorders Identification Test. Aligned so higher = lower risk.",
  },
  {
    id: "proqol", assessmentId: 6, name: "Quality of Life Indicator", abbr: "", group: "Clinical",
    domain: "Occupational", invert: false, ranges: RANGES.proqol,
    desc: "Professional quality of life — compassion satisfaction against compassion fatigue.",
    measures: "Professional Quality of Life scale. Higher = healthier.",
  },
  {
    id: "burnout", assessmentId: 39, name: "Burnout", abbr: "", group: "Behavioral",
    domain: "Recovery", invert: true, ranges: RANGES.burnout,
    desc: "Burnout load across the population — a key driver of recovery deficit.",
    measures: "Vasana Burnout Indicator. Shown in clinical direction (higher = more burnout); inverted for the index.",
  },
];

// Switchboard groups (order + labels for the Filters panel and scorecard sections).
export const SWITCHBOARD_GROUPS = [
  { label: "Behavioral", ids: ["vasana", "wellness", "burnout"] },
  { label: "Clinical", ids: ["insomnia", "anxiety", "depression", "ptsd", "support", "alcohol", "proqol"] },
];
export const ASSESSMENT_BY_ID = Object.fromEntries(ASSESSMENTS.map((a) => [a.id, a]));

// ---- Index helpers ----
// Active, scored (non-categorical) assessments that actually have data in scope.
export function scoredActive(assessments, active) {
  return assessments.filter((a) => active[a.id] && !a.categorical && a.responses > 0 && a.aligned != null);
}

export function computeIndex(assessments, active) {
  const scored = scoredActive(assessments, active);
  if (scored.length === 0) return null;
  const nowMean = scored.reduce((s, a) => s + a.aligned, 0) / scored.length;
  const withPrior = scored.filter((a) => a.priorAligned != null);
  let delta = 0;
  if (withPrior.length) {
    const nm = withPrior.reduce((s, a) => s + a.aligned, 0) / withPrior.length;
    const pm = withPrior.reduce((s, a) => s + a.priorAligned, 0) / withPrior.length;
    delta = Math.round(nm - pm);
  }
  return { value: Math.round(nowMean), delta, count: scored.length };
}

export function bandFor(value) {
  if (value >= 80) return { label: "Thriving", tone: "good" };
  if (value >= 65) return { label: "Healthy", tone: "good" };
  if (value >= 50) return { label: "Watch", tone: "watch" };
  return { label: "At Risk", tone: "risk" };
}

export function bandColor(tone) {
  return tone === "good" ? "#6fd592" : tone === "risk" ? "#ff8b73" : "var(--vas-warning)";
}

// ---- Readiness band system (single organizational readiness score) — V2 ----
// The Organizational Readiness Index maps its 0–100 value onto these 5 neon bands.
export const READINESS_BANDS = [
  { min: 0, max: 39, label: "Strained", color: "#FF5C57" },
  { min: 40, max: 59, label: "Needs Attention", color: "#FF8C42" },
  { min: 60, max: 74, label: "Steady", color: "#4AE07A" },
  { min: 75, max: 89, label: "Strong", color: "#45B4FF" },
  { min: 90, max: 100, label: "Thriving", color: "#C670F0" },
];
export function readinessBand(value) {
  return READINESS_BANDS.find((b) => value >= b.min && value <= b.max) || READINESS_BANDS[0];
}

// ---- Neon score color (V2) — health-aligned, higher = healthier ----
// Readiness Indicator uses the 5-band readiness scale (system A); every other
// scored assessment uses the 4-tier severe→healthy scale (system B). Categorical
// (Vasana Type) uses the brand purple.
// Resolve a score to its band. Two band shapes exist and both must work:
//   * contiguous (dd-data): 0–25, 25–50, 50–75, 75–100 — boundaries are shared, so
//     they are half-open [min, max) and 75 belongs to the UPPER band.
//   * inclusive (READINESS_BANDS / SCORE_BANDS_B): 0–39, 40–59 — 39 belongs to 0–39.
// The top band is always inclusive of its max (100).
export function bandAt(bands, score) {
  if (!bands || !bands.length || score == null) return null;
  const last = bands[bands.length - 1];
  if (score >= last.min) return last;
  const halfOpen = bands.find((b) => score >= b.min && score < b.max);
  if (halfOpen) return halfOpen;
  // Inclusive sets: a score sitting exactly on a band's max fails the `< max` test.
  return bands.find((b) => score >= b.min && score <= b.max) || bands[0];
}

// The band a score actually falls into, taken from the SAME band set the drawer's
// Score Legend renders (`scoreBands`). This is the single source of truth for both
// the band label on the card and its colour, so the score, the chip, and the
// highlighted legend row can never disagree.
export function scoreBandFor(a) {
  if (a.categorical || a.score == null) return null;
  return bandAt(scoreBands(a), a.score);
}

export function esScoreColor(a) {
  if (a.categorical) return "var(--vas-primary)";
  const band = scoreBandFor(a);
  if (band) return band.color;
  return "rgba(255,255,255,0.3)";
}

// ---- Score legend (drawer) — V2 ----
// System B fallback tiers (health-aligned: higher = healthier).
export const SCORE_BANDS_B = [
  { min: 0, max: 39, label: "At risk", color: "#FF5C57" },
  { min: 40, max: 59, label: "Needs attention", color: "#FF8C42" },
  { min: 60, max: 74, label: "Moderate", color: "#FFD84A" },
  { min: 75, max: 100, label: "Healthy", color: "#4AE07A" },
];
// Executive-summary assessment id → Deep Dive (instrument) id.
const ES_TO_DD = {
  wellness: "wellness", insomnia: "isi", anxiety: "gad", depression: "phq",
  ptsd: "pcl", support: "mspss", alcohol: "audit", proqol: "proqol", burnout: "vbi",
};
// Ordered band legend for a scored assessment (null for categorical), using that
// instrument's own named bands + thresholds from the Deep Dive model.
export function scoreBands(a) {
  if (a.categorical) return null;
  const dd = DD.data[ES_TO_DD[a.id] || a.id];
  if (dd && dd.bands) {
    const sys = ddSystemFor(dd.id);
    return dd.bands.map((b, i) => ({
      min: b.lo, max: b.hi, label: b.label,
      color: b.color || ddBandColor(i, dd.bands.length, !!dd.higherWorse, sys),
    }));
  }
  return a.id === "wellness" ? READINESS_BANDS : SCORE_BANDS_B;
}
// True when a higher raw score means worse (Burnout). Bands sit on the raw axis.
export function scoreHigherWorse(a) {
  const dd = DD.data[ES_TO_DD[a.id] || a.id];
  return !!(dd && dd.higherWorse);
}

// Type legend for the categorical Vasana Type disposition profile.
export const TYPE_LEGEND = [
  { type: "SA", label: "Sa — Structure" },
  { type: "RE", label: "Re — Influence" },
  { type: "GA", label: "Ga — Power" },
  { type: "MA", label: "Ma — Analysis" },
];

// "What this signals" for the Vasana Type card is written around whichever type is
// dominant in the live mix. Each entry completes the sentence:
//   "<TYPE> is the dominant disposition — <descriptor>"
// Only RE is authored (from the design). Types without a descriptor fall back to
// the bare sentence rather than inventing a behavioral claim.
export const VASANA_TYPE_SIGNAL = {
  RE: "adaptive, fast-reading, flight-leaning — meaning the population tends to respond to stress by adjusting and moving rather than holding position.",
  SA: null,
  GA: null,
  MA: null,
};

// Compose the Vasana Type "what this signals" copy from the live dominant type.
export function vasanaSignal(dominant) {
  const base = "How the organization naturally moves under pressure.";
  if (!dominant) return base;
  const descriptor = VASANA_TYPE_SIGNAL[dominant];
  return descriptor
    ? `${base} ${dominant} is the dominant disposition — ${descriptor}`
    : `${base} ${dominant} is the dominant disposition.`;
}
